// A color-banded score bar: the risk zones as a gradient track, with a marker
// at the patient's score. Shows distance-to-threshold, which a chip cannot — a
// Morse of 26 and a Morse of 44 are both "Moderate" but sit very differently.
import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";

// Deliberately the same three-state palette the vitals thresholds use, so
// "amber means watch this" reads consistently across the dashboard.
const LEVEL_COLOR = { Low: "#2e7d32", Moderate: "#ed6c02", High: "#d32f2f" };

export default function ScoreBar({ label, score, min = 0, max, bands, level, hint }) {
  const scored = score != null && !Number.isNaN(Number(score));
  const span = max - min;
  const pct = (v) => (span > 0 ? ((Math.min(Math.max(v, min), max) - min) / span) * 100 : 0);

  // Bands arrive in ascending score order; each occupies from the previous
  // band's ceiling to its own.
  let cursor = min;
  const segments = bands.map((band) => {
    const width = pct(band.upTo) - pct(cursor);
    cursor = band.upTo;
    return { ...band, width };
  });

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: "baseline", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: scored ? LEVEL_COLOR[level] : "text.disabled" }}>
          {scored ? `${score}${level ? ` · ${level}` : ""}` : "Not scored"}
        </Typography>
      </Stack>

      <Tooltip title={hint || ""} placement="top">
        <Box sx={{ position: "relative", height: 8, borderRadius: 4, overflow: "hidden", display: "flex" }}>
          {segments.map((seg) => (
            <Box
              key={seg.level + seg.upTo}
              sx={{
                width: `${seg.width}%`,
                bgcolor: LEVEL_COLOR[seg.level],
                // Unscored bars stay visible but recede, so an empty
                // instrument doesn't read as a green (reassuring) result.
                opacity: scored ? 0.35 : 0.12,
              }}
            />
          ))}
          {scored && (
            <Box
              sx={{
                position: "absolute",
                top: -2,
                left: `${pct(Number(score))}%`,
                transform: "translateX(-50%)",
                width: 3,
                height: 12,
                borderRadius: 1,
                bgcolor: LEVEL_COLOR[level] || "#333",
                boxShadow: "0 0 0 1.5px #fff",
              }}
            />
          )}
        </Box>
      </Tooltip>
    </Box>
  );
}
